import * as THREE from 'three';
import type { CombatVisualEvent } from '../../../shared/game/combat-types.js';
import { groundDecal } from '../environment/ground-decal.js';
import {
  blowDirection, fountainDroplets, isDecapitation, launchHead, mulberry32, poolFor, sneakGore, stainFrom, stepDroplets, stepHead, woundDroplets, woundOf,
  type Droplet, type Fountain, type HeadBody, type SeveredHead, type Stain,
} from './blood-model.js';

const MAX_DROPLETS = 720, MAX_STAINS = 96, MAX_HEADS = 6;
const FRESH = new THREE.Color(0x9a1218), DARK = new THREE.Color(0x4a0709), DRIED = new THREE.Color(0x2e0608);
const UP = new THREE.Vector3(0, 1, 0);

/** What the effects need to know about the unit a blow landed on. */
export interface BloodTarget {
  strength: number; height: number; radius: number;
  /** Takes the head off the unit's figure, if it has one to lose. */
  sever?: () => SeveredHead | undefined;
}
interface LiveStain extends Stain { age: number; lift: number }
interface LiveHead { body: HeadBody; group: THREE.Group; fade: number }

/**
 * Blood from slashes, arrow wounds and killing blows, and the heads that come off them. Presentation only: it reacts to the
 * server's visual events, runs on real time, and owns every mesh it adds to the scene.
 */
export class BloodEffects {
  private droplets: Droplet[] = [];
  private stains: LiveStain[] = [];
  private fountains: Fountain[] = [];
  private heads: LiveHead[] = [];
  private rng = mulberry32(1);
  private mesh: THREE.InstancedMesh;
  private ground = new THREE.Group();
  private blobs: THREE.BufferGeometry[];
  /** One instanced draw per blob shape for every stain on the ground, instead of a mesh (and a draw) each: a long fight left 96. */
  private stainMeshes: THREE.InstancedMesh[];
  private euler = new THREE.Euler();
  private scratch = { matrix: new THREE.Matrix4(), position: new THREE.Vector3(), quaternion: new THREE.Quaternion(), scale: new THREE.Vector3(), color: new THREE.Color(), velocity: new THREE.Vector3() };
  constructor(private scene: THREE.Scene, private groundHeight: (x: number, z: number) => number = () => 0) {
    // An empty instanced mesh stays in the scene, so its shader is compiled with the rest before play begins.
    this.mesh = new THREE.InstancedMesh(new THREE.IcosahedronGeometry(1, 0), new THREE.MeshBasicMaterial({ color: 0xffffff }), MAX_DROPLETS);
    this.mesh.name = 'blood-droplets'; this.mesh.count = 0; this.mesh.frustumCulled = false;
    this.mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(MAX_DROPLETS * 3), 3);
    this.ground.name = 'blood-stains';
    this.blobs = [0, 1, 2, 3].map(seed => blobGeometry(seed));
    const material = stainMaterial();
    this.stainMeshes = this.blobs.map(blob => {
      const mesh = groundDecal(new THREE.InstancedMesh(blob, material, MAX_STAINS)); mesh.count = 0; mesh.frustumCulled = false;
      mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(MAX_STAINS * 3), 3);
      blob.setAttribute('instanceAlpha', new THREE.InstancedBufferAttribute(new Float32Array(MAX_STAINS), 1));
      this.ground.add(mesh); return mesh;
    });
    scene.add(this.mesh, this.ground);
  }

  /** A blow landed. Bleeds where the blow does; a lethal critical slash also takes the head. */
  hit(event: CombatVisualEvent, target: BloodTarget) {
    const wound = woundOf(event);
    if (!wound) return;
    const rng = this.rng = mulberry32(event.id * 2654435761);
    const d = blowDirection(event);
    const head = isDecapitation(event) ? target.sever?.() : undefined;
    // A head taken off is struck at the neck; a blow with nothing to take bleeds from the chest, and harder.
    const strength = isDecapitation(event) && !head ? Math.min(1, target.strength + .3) : target.strength;
    const drops = woundDroplets(event, wound, strength, target.height, target.radius, rng, head ? head.neck.y : undefined);
    if (!head) for (const drop of drops) drop.y += this.groundHeight(drop.x, drop.z);
    this.add(drops);
    if (event.sneakAttack) {
      const gore = sneakGore(event, target.strength, target.height, target.radius, rng), ground = this.groundHeight(gore.gush.x, gore.gush.z);
      for (const drop of gore.drops) drop.y += ground;
      gore.gush.y += ground;
      this.add(gore.drops); this.fountains.push(gore.gush);
      if (!event.lethal) this.stain({ x: event.to.x, z: event.to.z, radius: .2 + target.radius * .45, delay: .35, grow: 1.6, life: 50, shade: .3, seed: event.id });
    }
    if (event.lethal) this.stain(poolFor(event, target.strength, target.radius, rng, !!head));
    if (!head) return;
    this.add(woundDroplets(event, 'slash', 1, target.height, target.radius, rng, head.neck.y));
    this.fountains.push({ x: head.neck.x, y: head.neck.y, z: head.neck.z, lx: d.x * .9, lz: d.z * .9, age: 0, life: 2.1, owed: 0, follow: head.stump });
    this.detach(event, head);
  }

  clear() {
    this.droplets = []; this.fountains = [];
    this.stains = []; this.drawStains();
    for (const head of this.heads) this.discard(head);
    this.heads = []; this.mesh.count = 0;
  }
  /** Advances by `dt` real seconds (a hitch never skips more than a few frames' worth of fall). */
  update(dt: number) {
    dt = Math.min(Math.max(dt, 0), .05);
    if (!dt) return;
    for (const fountain of this.fountains) {
      fountain.age += dt;
      // The blood comes from the stump wherever the falling body has taken it; with no body to follow it sinks as the body would.
      const at = fountain.follow?.();
      if (at) { fountain.x = at.x; fountain.y = at.y; fountain.z = at.z; } else fountain.y -= Math.min(fountain.age, 1) * dt * .9;
      this.add(fountainDroplets(fountain, dt, this.rng));
    }
    this.fountains = this.fountains.filter(fountain => fountain.age < fountain.life);
    this.droplets = stepDroplets(this.droplets, dt, drop => { const stain = stainFrom(drop, this.rng); if (stain) this.stain(stain); }, this.groundHeight);
    this.draw();
    for (const stain of this.stains) stain.age += dt;
    this.stains = this.stains.filter(stain => stain.age < stain.life);
    this.drawStains();
    for (const head of [...this.heads]) {
      const before = head.body.resting;
      stepHead(head.body, dt, hardness => { this.stain({ x: head.body.x, z: head.body.z, radius: .12 + Math.min(.14, hardness * .03), delay: 0, grow: .15, life: 60, shade: .2, seed: head.body.bounces }); this.add(burstAt(head.body, Math.min(1, hardness / 6), this.rng)); }, this.groundHeight);
      if (!head.body.resting) {
        head.body.trail += dt;
        if (head.body.trail > .035) { head.body.trail = 0; this.add([{ x: head.body.x, y: head.body.y, z: head.body.z, vx: (this.rng() - .5) * .8, vy: this.rng(), vz: (this.rng() - .5) * .8, size: .03 + this.rng() * .03, age: 0, life: 1.2, tone: .1 }]); }
      } else if (!before) this.stain({ x: head.body.x, z: head.body.z, radius: .36, delay: .2, grow: 3, life: 70, shade: .1, seed: 7 });
      head.group.position.set(head.body.x, head.body.y, head.body.z);
      head.group.rotation.x += head.body.spin.x * dt; head.group.rotation.y += head.body.spin.y * dt; head.group.rotation.z += head.body.spin.z * dt;
      // A head lies where it fell for a long while, then sinks out of the world.
      if (head.body.age > 40) head.fade += dt / 2.5;
      head.group.scale.setScalar(Math.max(.001, 1 - head.fade));
      if (head.fade >= 1) { this.discard(head); this.heads.splice(this.heads.indexOf(head), 1); }
    }
  }

  private add(list: Droplet[]) {
    this.droplets.push(...list);
    if (this.droplets.length > MAX_DROPLETS) this.droplets.splice(0, this.droplets.length - MAX_DROPLETS);
  }
  private draw() {
    const { matrix, position, quaternion, scale, color, velocity } = this.scratch;
    let n = 0;
    for (const drop of this.droplets) {
      if (drop.age < 0) continue;
      // Droplets stretch along their flight, so a spray reads as streaks rather than beads.
      velocity.set(drop.vx, drop.vy, drop.vz);
      const speed = velocity.length(), stretch = 1 + Math.min(1.4, speed * .16), shrink = Math.min(1, (drop.life - drop.age) / .25);
      if (speed > .01) quaternion.setFromUnitVectors(UP, velocity.multiplyScalar(1 / speed)); else quaternion.identity();
      scale.set(drop.size, drop.size * stretch, drop.size).multiplyScalar(Math.max(.05, shrink));
      matrix.compose(position.set(drop.x, drop.y, drop.z), quaternion, scale);
      this.mesh.setMatrixAt(n, matrix); this.mesh.setColorAt(n, color.copy(DARK).lerp(FRESH, 1 - drop.tone * .85)); n++;
    }
    this.mesh.count = n;
    this.mesh.instanceMatrix.needsUpdate = true; if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
  }
  /** A ground stain for compiling its shader before play: the first blood of a fight otherwise linked it mid-fight. */
  warmup(): THREE.Object3D {
    const mesh = groundDecal(new THREE.InstancedMesh(this.blobs[0], this.stainMeshes[0].material, 1));
    mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(3), 3); return mesh;
  }
  private stain(stain: Stain) {
    if (this.stains.length >= MAX_STAINS) this.stains.shift();
    // Stains laid one after another sit a hair apart, so overlapping pools never flicker against each other.
    this.stains.push({ ...stain, age: 0, lift: .03 + (this.stains.length % 12) * .0008 });
  }
  /** Writes every stain into its blob's instances: grown in after its delay, bright when wet, dark when dry, fading at the end. */
  private drawStains() {
    const { matrix, position, quaternion, scale, color } = this.scratch, euler = this.euler;
    for (const mesh of this.stainMeshes) mesh.count = 0;
    for (const stain of this.stains) {
      if (stain.age < stain.delay) continue;
      const mesh = this.stainMeshes[stain.seed % this.stainMeshes.length], i = mesh.count++;
      const t = THREE.MathUtils.clamp((stain.age - stain.delay) / stain.grow, 0, 1), eased = 1 - (1 - t) ** 3;
      quaternion.setFromEuler(euler.set(-Math.PI / 2, 0, stain.seed * 2.399));
      matrix.compose(position.set(stain.x, this.groundHeight(stain.x, stain.z) + stain.lift, stain.z), quaternion, scale.setScalar(Math.max(.001, stain.radius * eased)));
      mesh.setMatrixAt(i, matrix);
      mesh.setColorAt(i, color.copy(DARK).lerp(FRESH, Math.max(0, 1 - stain.age / 6) * .6).lerp(DRIED, THREE.MathUtils.clamp(stain.age / 40, 0, 1) * .6));
      (mesh.geometry.getAttribute('instanceAlpha') as THREE.InstancedBufferAttribute).setX(i, .9 * Math.min(1, (stain.life - stain.age) / 4));
    }
    for (const mesh of this.stainMeshes) {
      mesh.visible = mesh.count > 0;
      mesh.instanceMatrix.needsUpdate = true; mesh.instanceColor!.needsUpdate = true; mesh.geometry.getAttribute('instanceAlpha').needsUpdate = true;
    }
  }
  /** The severed head: a skull in the dead unit's own skin and hair, with the cut neck showing. */
  private detach(event: CombatVisualEvent, head: SeveredHead) {
    if (this.heads.length >= MAX_HEADS) this.discard(this.heads.shift()!);
    const body = launchHead(event, head, this.rng), group = new THREE.Group(), r = head.radius;
    const skin = new THREE.MeshStandardMaterial({ color: head.skin, roughness: .75 }), hair = new THREE.MeshStandardMaterial({ color: head.hair, roughness: .9 });
    const flesh = new THREE.MeshStandardMaterial({ color: 0x7a0f12, roughness: .35 }), dark = new THREE.MeshStandardMaterial({ color: 0x1d1a1a, roughness: 1 });
    const skull = new THREE.Mesh(new THREE.SphereGeometry(r, 14, 10), skin); skull.scale.set(.92, 1.08, 1); group.add(skull);
    const jaw = new THREE.Mesh(new THREE.SphereGeometry(r * .78, 10, 8), skin); jaw.position.set(0, -r * .5, r * .22); jaw.scale.set(.9, .8, .9); group.add(jaw);
    const cap = new THREE.Mesh(new THREE.SphereGeometry(r * 1.06, 14, 8, 0, Math.PI * 2, 0, Math.PI * .56), hair); cap.scale.set(.93, 1.08, 1.04); cap.rotation.x = -.35; group.add(cap);
    const nose = new THREE.Mesh(new THREE.ConeGeometry(r * .16, r * .34, 5), skin); nose.rotation.x = Math.PI / 2; nose.position.set(0, -r * .05, r * 1.02); group.add(nose);
    for (const side of [-1, 1]) { const eye = new THREE.Mesh(new THREE.SphereGeometry(r * .07, 6, 4), dark); eye.position.set(side * r * .32, r * .18, r * .9); group.add(eye); }
    const neck = new THREE.Mesh(new THREE.CylinderGeometry(r * .42, r * .5, r * .5, 10), skin); neck.position.set(0, -r * .85, -r * .12); group.add(neck);
    const cut = new THREE.Mesh(new THREE.CircleGeometry(r * .43, 10), flesh); cut.rotation.x = Math.PI / 2; cut.position.set(0, -r * 1.1, -r * .12); group.add(cut);
    group.traverse(object => { if (object instanceof THREE.Mesh) { object.castShadow = true; object.frustumCulled = false; } });
    group.quaternion.setFromUnitVectors(UP, new THREE.Vector3(head.up.x, head.up.y, head.up.z).normalize());
    group.position.set(body.x, body.y, body.z);
    this.scene.add(group); this.heads.push({ body, group, fade: 0 });
  }
  private discard(head: LiveHead) {
    this.scene.remove(head.group);
    const geometries = new Set<THREE.BufferGeometry>(), materials = new Set<THREE.Material>();
    head.group.traverse(child => { if (child instanceof THREE.Mesh) { geometries.add(child.geometry); materials.add(child.material as THREE.Material); } });
    for (const geometry of geometries) geometry.dispose();
    for (const material of materials) material.dispose();
  }
  /** Counts for tests and diagnostics. */
  get live() { return { droplets: this.droplets.length, stains: this.stains.length, heads: this.heads.length, fountains: this.fountains.length }; }
  get visualObjects(): THREE.Object3D[] { return [this.mesh, this.ground, ...this.heads.map(head => head.group)]; }
  dispose() {
    this.clear(); this.scene.remove(this.mesh, this.ground);
    this.mesh.geometry.dispose(); (this.mesh.material as THREE.Material).dispose(); this.mesh.dispose();
    (this.stainMeshes[0].material as THREE.Material).dispose(); for (const mesh of this.stainMeshes) mesh.dispose();
    for (const blob of this.blobs) blob.dispose();
  }
}

/**
 * The stains' shared material: each instance carries its colour and an opacity of its own (`instanceAlpha`), so one draw per
 * blob shape fades every stain at its own pace.
 */
function stainMaterial() {
  const material = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, side: THREE.DoubleSide });
  material.customProgramCacheKey = () => 'blood-stain';
  material.onBeforeCompile = shader => {
    shader.vertexShader = 'attribute float instanceAlpha;\nvarying float vInstanceAlpha;\n' + shader.vertexShader.replace('#include <begin_vertex>', '#include <begin_vertex>\n  vInstanceAlpha = instanceAlpha;');
    shader.fragmentShader = 'varying float vInstanceAlpha;\n' + shader.fragmentShader.replace('vec4 diffuseColor = vec4( diffuse, opacity );', 'vec4 diffuseColor = vec4( diffuse, opacity * vInstanceAlpha );');
  };
  return material;
}

/** A small burst where a head strikes the ground. */
function burstAt(head: HeadBody, force: number, rng: () => number): Droplet[] {
  return Array.from({ length: Math.round(4 + 8 * force) }, () => {
    const angle = rng() * Math.PI * 2, speed = .8 + rng() * 2.2 * (.5 + force);
    return { x: head.x, y: head.y - head.radius * .6, z: head.z, vx: Math.cos(angle) * speed, vy: .8 + rng() * 2.2, vz: Math.sin(angle) * speed, size: .026 + rng() * .035, age: 0, life: 1, tone: rng() * .5 };
  });
}

/** A smooth, slightly irregular unit-radius blob lying in the XY plane (rotated onto the ground when used). */
function blobGeometry(seed: number) {
  const rng = mulberry32(seed * 7919 + 13), shape = new THREE.Shape(), steps = 72;
  // A few low harmonics make an organic puddle outline; the high count keeps the edge round at any size.
  const waves = [2, 3, 5].map(frequency => ({ frequency, amplitude: .16 / (frequency - 1) * (.6 + rng() * .8), phase: rng() * Math.PI * 2 }));
  for (let i = 0; i < steps; i++) {
    const angle = i / steps * Math.PI * 2, radius = .92 + waves.reduce((sum, wave) => sum + wave.amplitude * Math.sin(angle * wave.frequency + wave.phase), 0);
    const x = Math.cos(angle) * radius, y = Math.sin(angle) * radius;
    if (i === 0) shape.moveTo(x, y); else shape.lineTo(x, y);
  }
  shape.closePath();
  return new THREE.ShapeGeometry(shape);
}
